import React, { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Select,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "./Support.css";

// Sent ONCE. These writes are not idempotent: saving the prompts INSERTS a new version
// and the responder reads the newest, and posting an article inserts another one. The
// transport repeats a write whose answer was lost, so a blip could put a superseded
// configuration back on top of a colleague's newer one — every AI turn afterwards using
// a prompt nobody chose — or leave two copies of the same article in the knowledge base.
// A person watching a failure can decide to try again; the transport cannot.
const SAVE_ONCE = { attempts: 1 };
// A bound on every write from this page. `Auth.js` deliberately leaves writes unbounded by
// default, because a timed-out POST is retried by its loop and only an idempotent caller can
// afford that — but these already ask for a single attempt, so a deadline costs nothing and
// buys the one thing missing: a request the server accepted and never finished answering
// leaves `saving` true forever, the button disabled, and the author unable to learn whether
// their work was stored.
const WRITE_TIMEOUT_MS = 120000;


const EMPTY_ARTICLE = {
  title: "",
  body: "",
  paths: "",
  keywords: "",
  // Who the assistant may repeat this to. 'navigation' means only somebody who can
  // reach every page the article names; 'everyone' is general help. The safe default
  // matches the server's, and an article with neither pages nor 'everyone' reaches
  // nobody — which is why this control has to be here rather than left implicit.
  audience: "navigation",
  pms: "",
  enabled: true,
};

const toList = (value) =>
  (value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const toText = (value) => (Array.isArray(value) ? value.join(", ") : value || "");

function SupportArticles() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelDeleteRef = useRef(null);

  const [articles, setArticles] = useState([]);
  const [knowledge, setKnowledge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState(EMPTY_ARTICLE);
  // Which list read is the newest. A ref rather than state: it is read inside the request
  // that raced, and must be the value at THAT moment, not the one a render settled on.
  const listIssuedRef = useRef(0);
  // Which editor session is on screen. A save is awaited while the modal stays closable,
  // so an earlier save's answer could close — and then reset — a DIFFERENT article's form
  // that had been opened since, taking its unsaved draft with it. Bumped on every open.
  const editorRef = useRef(0);

  const notifyError = (description) =>
    toast({ title: "Error", description, status: "error", duration: 3000, isClosable: true });

  const fetchArticles = async () => {
    setLoading(true);
    // Numbered on the way OUT, and only the newest read may paint. Every write on this
    // page triggers one of these, so they overlap: toggle A and then delete B, and B's
    // reload can have been ISSUED before A's toggle committed. Landing last, it puts the
    // pre-toggle row back — the wrong enabled state on screen, and a stale `updated` with
    // it, so the administrator's next edit, toggle or delete is refused against a version
    // nobody is looking at. The thread, inbox and widget lists are all ordered this way;
    // this list was not.
    const at = (listIssuedRef.current += 1);
    try {
      const response = await cqAPI.get("/support-admin/articles");
      if (at !== listIssuedRef.current) return;
      if (response?.type === "ok" && Array.isArray(response.data)) {
        setArticles(response.data);
      } else {
        notifyError(response?.msg || "Failed to load support articles.");
      }
    } catch (error) {
      if (at !== listIssuedRef.current) return;
      console.error("Error loading support articles:", error);
      notifyError("Failed to load support articles.");
    } finally {
      // The spinner belongs to whichever read is still outstanding, so an overtaken one
      // must not clear it and leave a newer read painting behind a settled page.
      if (at === listIssuedRef.current) setLoading(false);
    }
  };

  // How fresh the assistant's other source of truth is. The route inventory is
  // republished by vivaapp's deploy, so a stale timestamp here means the assistant
  // does not know about pages added since.
  const fetchKnowledge = async () => {
    try {
      const response = await cqAPI.get("/support-admin/knowledge");
      if (response?.type === "ok" && response.data) setKnowledge(response.data);
    } catch (error) {
      console.error("Error loading support knowledge status:", error);
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchKnowledge();
  }, []);

  // The one way the editor is opened. Both callers go through it so a new session is
  // counted whichever way the modal was reached — the create path had the same defect
  // the edit path did, and two separate `onOpen()` calls are how it got there.
  // Uploading a user guide. A PDF or Word file becomes an ordinary article, so everything
  // on this page then applies to it — edit the extracted text, switch it off, delete it.
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadDocument = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);

      // A single attempt, and no deadline.
      //
      // No deadline because a ten-megabyte PDF over a slow link legitimately takes
      // minutes, and a bound would abandon uploads that were going to succeed. A single
      // attempt because this write is NOT idempotent — it inserts a document — so the
      // transport repeating it after a lost answer files another copy of the same guide,
      // and the assistant then retrieves and quotes it twice. The transport retries five
      // times by default.
      const response = await cqAPI.post("/support-admin/documents", form, undefined, SAVE_ONCE);
      if (response?.type === "ok") {
        toast({
          title: "Uploaded",
          description: response.data?.truncated
            ? `${response.data.title} — stored, but ${response.data.truncated.toLocaleString()} characters were too long to keep.`
            : `${response.data?.title || "Document"} added to the knowledge base.`,
          status: response.data?.truncated ? "warning" : "success",
          duration: response.data?.truncated ? 10000 : 4000,
          isClosable: true,
        });
        await fetchArticles();
      } else {
        notifyError(response?.msg || "Could not upload that document.");
      }
    } catch (error) {
      console.error("Error uploading support document:", error);
      notifyError("Could not upload that document.");
    } finally {
      setUploading(false);
      // Cleared so choosing the SAME file again still fires a change event; without this
      // a failed upload cannot be retried without picking a different file first.
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  /**
   * Save one document, or the whole knowledge base, as markdown.
   *
   * Entirely client-side: the list endpoint already returns every body, so a download
   * endpoint would be a second copy of the same bytes and a second thing to keep
   * permission-checked. The object URL is revoked once the click has been taken —
   * without that, every download holds its blob in memory until the tab closes, and the
   * whole guide is a hundred kilobytes a time.
   */
  const saveAsFile = (filename, text) => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/markdown;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Safe as a filename on every platform, and never empty.
  const fileNameFor = (title) => `${String(title || "document")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "document"}.md`;

  const downloadOne = (article) => {
    // The disabled state travels with it. A document switched off is not one the
    // assistant uses, and a copy that does not say so reads as current documentation.
    const header = [
      `# ${article.title}`,
      "",
      `> ${article.enabled === false ? "DISABLED — the assistant does not use this" : "Active"}`,
      `> Audience: ${article.audience === "everyone" ? "everyone" : "only users whose menu contains its paths"}`,
      ...(article.paths || []).length ? [`> Pages: ${(article.paths || []).join(", ")}`] : [],
      `> Last updated: ${article.updated ? moment(article.updated).format("LLL") : "unknown"}`,
      "",
      "---",
      "",
    ].join("\n");
    saveAsFile(fileNameFor(article.title), header + (article.body || ""));
  };

  const downloadAll = () => {
    if (!articles.length) return;
    const parts = [
      "# Support knowledge base",
      "",
      `${articles.length} document(s), exported ${moment().format("LLL")}.`,
      "",
    ];
    articles.forEach((article) => {
      parts.push(
        "",
        "---",
        "",
        `# ${article.title}`,
        "",
        `> ${article.enabled === false ? "DISABLED — the assistant does not use this" : "Active"} · `
          + `audience: ${article.audience === "everyone" ? "everyone" : "navigation"}`
          + `${(article.paths || []).length ? ` · pages: ${(article.paths || []).join(", ")}` : ""}`,
        "",
        article.body || "",
      );
    });
    saveAsFile(`support-knowledge-${moment().format("YYYY-MM-DD")}.md`, parts.join("\n"));
  };

  const openEditor = () => {
    editorRef.current += 1;
    onOpen();
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_ARTICLE);
    openEditor();
  };

  const openEdit = (article) => {
    setEditingId(article._id);
    setForm({
      title: article.title || "",
      body: article.body || "",
      paths: toText(article.paths),
      keywords: toText(article.keywords),
      audience: article.audience || "navigation",
      pms: toText(article.pms),
      enabled: article.enabled !== false,
      // The version this form was opened against. The save replaces the whole
      // document, so the server refuses it if somebody else has saved since.
      updated: article.updated || null,
    });
    openEditor();
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      notifyError("A title is required.");
      return;
    }
    setSaving(true);
    // The editor session this save belongs to. Its answer may only act on the modal it
    // was started from: closing, or resetting the form, on a LATER one takes that
    // article's unsaved draft with it.
    const session = editorRef.current;
    const payload = {
      title: form.title.trim(),
      body: form.body,
      paths: toList(form.paths),
      keywords: toList(form.keywords),
      // Sent on every save. The server reads an absent value as 'navigation', so
      // omitting it silently narrowed an 'everyone' article each time it was edited.
      audience: form.audience === "everyone" ? "everyone" : "navigation",
      pms: toList(form.pms),
      enabled: !!form.enabled,
      // Only when editing: a create has no version to be stale against.
      ...(editingId && form.updated ? { updated: form.updated } : {}),
    };
    try {
      const response = editingId
        ? await cqAPI.put(`/support-admin/articles/${editingId}`, payload, WRITE_TIMEOUT_MS, SAVE_ONCE)
        : await cqAPI.post("/support-admin/articles", payload, WRITE_TIMEOUT_MS, SAVE_ONCE);
      if (response?.type === "ok") {
        toast({
          title: "Saved",
          description: editingId ? "Article updated." : "Article created.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Only if this is still the editor that started the save.
        if (session === editorRef.current) onClose();
        await fetchArticles();
      } else {
        notifyError(response?.msg || "Failed to save the article.");
        // Only when a VERSION moved, which the server tells us by handing back the
        // article it refused us against. The LIST is brought up to date; the open form
        // deliberately is not — silently rebasing it onto the colleague's version would
        // let the next click overwrite the work this guard just protected — and
        // refreshing the list is what makes reopening the article hand back the current
        // version, which is otherwise impossible without reloading the page.
        //
        // An ordinary validation refusal carries no article and moves nothing, so it
        // must NOT block here: waiting on an unrelated read leaves Save disabled after
        // the answer has already arrived, and can raise a second, unrelated error over
        // the one the author actually needs to read.
        if (response?.data) await fetchArticles();
      }
    } catch (error) {
      console.error("Error saving support article:", error);
      notifyError("Failed to save the article.");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (article) => {
    const next = !(article.enabled !== false);
    setArticles((prev) =>
      prev.map((a) => (a._id === article._id ? { ...a, enabled: next } : a))
    );
    try {
      const response = await cqAPI.put(`/support-admin/articles/${article._id}`, {
        title: article.title,
        body: article.body,
        paths: article.paths || [],
        keywords: article.keywords || [],
        // Carried, not defaulted. The update handler replaces the whole document, so
        // flipping the enable switch on an 'everyone' article used to quietly narrow
        // it to 'navigation' — and a pathless one then reached nobody at all.
        audience: article.audience === "everyone" ? "everyone" : "navigation",
        pms: article.pms || [],
        enabled: next,
        // The version this ROW was rendered from. Flipping a switch sends the whole
        // document back, so a list loaded before a colleague's edit would otherwise
        // put their work back the way it was, silently.
        ...(article.updated ? { updated: article.updated } : {}),
      }, WRITE_TIMEOUT_MS, SAVE_ONCE);
      if (response?.type === "ok" && response.data) {
        // The write replaced `updated`, and the server hands back what it stored. Adopt
        // it, or this row keeps the version it was rendered from and the NEXT toggle,
        // edit or delete is refused as stale against a change this administrator just
        // made themselves.
        //
        // ...but ONLY over the row this answer is actually about. A slow response can
        // land after something else has already loaded a newer article — a colleague's
        // save, fetched by any other operation on this page — and merging a stale answer
        // over it puts the older title, body and version back on screen, so the next
        // guarded write is refused against a version nobody is looking at. The row still
        // has to be the one this request was sent for.
        setArticles((prev) =>
          prev.map((a) => {
            if (a._id !== article._id) return a;
            const unchanged = String(a.updated ?? "") === String(article.updated ?? "");
            return unchanged ? { ...a, ...response.data } : a;
          })
        );
      }
      if (response?.type !== "ok") {
        notifyError(response?.msg || "Failed to update the article.");
        // Including the stale-version refusal: the optimistic flip above has to come
        // back off, and the list has to show what is actually stored.
        await fetchArticles();
      }
    } catch (error) {
      console.error("Error toggling support article:", error);
      notifyError("Failed to update the article.");
      await fetchArticles();
    }
  };

  const askDelete = (article) => {
    setPendingDelete(article);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const response = await cqAPI.del(
        `/support-admin/articles/${pendingDelete._id}`,
        // The version this dialog was opened against. Deleting is the destructive half
        // of the pair, so it carries the same guard the save does — a colleague's newer
        // edit must not be destroyed by a confirmation typed against the old one.
        { updated: pendingDelete.updated || null },
        WRITE_TIMEOUT_MS,
        SAVE_ONCE,
      );
      if (response?.type === "ok") {
        toast({ title: "Deleted", status: "success", duration: 3000, isClosable: true });
        await fetchArticles();
      } else {
        notifyError(response?.msg || "Failed to delete the article.");
        // Reload before the dialog closes. A version conflict is refused against the
        // `updated` this list was rendered from — so without this the row keeps the
        // stale value, reopening the dialog sends it again, and the administrator is
        // refused for ever with no way forward but a page reload.
        await fetchArticles();
      }
    } catch (error) {
      console.error("Error deleting support article:", error);
      notifyError("Failed to delete the article.");
      await fetchArticles();
    } finally {
      setDeleting(false);
      setPendingDelete(null);
      onDeleteClose();
    }
  };

  return (
    <Box className="support-page" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">
          Support Articles
        </Text>
        <Flex gap={2} wrap="wrap">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.md,.markdown,.txt"
            style={{ display: "none" }}
            onChange={(e) => uploadDocument(e.target.files && e.target.files[0])}
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current && fileRef.current.click()}
            isLoading={uploading}
            loadingText="Reading"
          >
            Upload a guide
          </Button>
          <Button
            variant="outline"
            onClick={downloadAll}
            isDisabled={!articles.length}
            title={articles.length ? "Save every document as one markdown file" : "Nothing to download yet"}
          >
            Download all
          </Button>
          <Button colorScheme="blue" onClick={openCreate}>
            New article
          </Button>
        </Flex>
      </Flex>

      {knowledge && (
        <Text fontSize="sm" color="gray.500" mb={4} textAlign="left">
          The assistant also reads the live menu plus{" "}
          <strong>{knowledge.routes}</strong> app routes published by the last vivaapp deploy
          {knowledge.app_version ? ` (${knowledge.app_version})` : ""}
          {knowledge.generated_at
            ? `, ${moment(knowledge.generated_at).fromNow()}`
            : " — never published yet"}
          .
        </Text>
      )}

      {loading ? (
        <Flex align="center" justify="center" py={10}>
          <Spinner mr={3} />
          <Text>Loading articles…</Text>
        </Flex>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="lg">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Title</Th>
                <Th>Paths</Th>
                <Th>Keywords</Th>
                <Th>Enabled</Th>
                <Th>Updated</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {articles.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text fontSize="sm" color="gray.500">
                      No articles yet.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                articles.map((article) => (
                  <Tr key={article._id}>
                    <Td maxW="260px" whiteSpace="normal">
                      {article.title}
                    </Td>
                    <Td maxW="220px" whiteSpace="normal" fontSize="xs">
                      <Text noOfLines={3}>{toText(article.paths) || "—"}</Text>
                    </Td>
                    <Td maxW="220px" whiteSpace="normal" fontSize="xs">
                      {/* The generated guide carries hundreds of keywords; unclamped,
                          this one cell was taller than the rest of the page. The full
                          list is still readable through Edit. */}
                      <Text noOfLines={3}>{toText(article.keywords) || "—"}</Text>
                    </Td>
                    <Td>
                      <Switch
                        isChecked={article.enabled !== false}
                        onChange={() => toggleEnabled(article)}
                        aria-label={`Toggle ${article.title}`}
                      />
                    </Td>
                    <Td whiteSpace="nowrap">
                      <Tooltip label={moment(article.updated || article.created).format("LLL")} hasArrow>
                        <Text as="span">
                          {article.updated || article.created
                            ? moment(article.updated || article.created).fromNow()
                            : "—"}
                        </Text>
                      </Tooltip>
                    </Td>
                    <Td whiteSpace="nowrap">
                      <Button size="xs" mr={2} onClick={() => openEdit(article)}>
                        Edit
                      </Button>
                      <Button size="xs" mr={2} variant="outline" onClick={() => downloadOne(article)}>
                        Download
                      </Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => askDelete(article)}>
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "3xl" }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? "Edit article" : "New article"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="How to add a phone number"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Body (markdown)</FormLabel>
                <Textarea
                  minH="240px"
                  resize="vertical"
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Markdown content the assistant can quote from..."
                />
              </FormControl>
              <FormControl>
                <FormLabel>Paths (comma separated)</FormLabel>
                <Input
                  value={form.paths}
                  onChange={(e) => setForm({ ...form, paths: e.target.value })}
                  placeholder="/phones/numbers, /settings/booking"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Keywords (comma separated)</FormLabel>
                <Input
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                  placeholder="phone number, porting, caller id"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Only for practices using this software</FormLabel>
                <Input
                  value={form.pms}
                  onChange={(e) => setForm({ ...form, pms: e.target.value })}
                  placeholder="denticon, dentrix, eaglesoft — leave blank for all"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {toList(form.pms).length
                    ? `Only practices connected to ${toList(form.pms).join(" or ")} will be told about this.`
                    : "Blank means every practice. Fill this in for a guide about setting up someone else's software — those instructions are wrong, and convincingly so, for a practice running a different system."}
                </Text>
              </FormControl>
              <FormControl>
                <FormLabel>Who the assistant may show this to</FormLabel>
                <Select
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })}
                >
                  <option value="navigation">
                    Only people who can reach every page listed above
                  </option>
                  <option value="everyone">Everyone — general product help</option>
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {form.audience === "everyone"
                    ? "This article can be quoted to any customer."
                    : (toList(form.paths).length
                      ? "Customers without one of those pages will not be told about this."
                      : "No pages listed, so this article will not be shown to anyone.")}
                </Text>
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Enabled</FormLabel>
                <Switch
                  isChecked={!!form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving} loadingText="Saving">
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelDeleteRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete article
            </AlertDialogHeader>
            <AlertDialogBody>
              Delete {pendingDelete?.title || "this article"}? The assistant will stop using it.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={confirmDelete} isLoading={deleting}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default SupportArticles;
